<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { gql } from "@apollo/client/core";
import apolloClient from "@/apollo";
import CustomModal from "../components/CustomModal.vue";

// GraphQL Query to get all reservations
const GET_ALL_RESERVATIONS = gql`
  query GetAllReservations {
    getAllReservations {
      status
      message
      data {
        id
        status
        arriveDate
        departureDate
        totalPrice
        accomodation {
          id
          name
          region
        }
        client {
          firstName
          lastName
          phone
          email
           
          id
        }
        docReservation {
          id
          docUrl
          createdAt
        }
      }
    }
  }
`;

// GraphQL Query to get accommodations
const GET_ACCOMODATIONS = gql`
  query GetAccommodations {
    getAccomodations {
      status
      data {
        id
        name
        type
      }
    }
  }
`;

// GraphQL Query to get reservation stats
const GET_RESERVATION_STATS = gql`
  query GetReservationStats {
    getReservationStats {
      totalConfirmed
      guesthouseConfirmedCount
      campingConfirmedCount
      totalConfirmedAmount
      guesthousePendingCount
      campingPendingCount
    }
  }
`;

// GraphQL Query to get reservation by ID
const GET_RESERVATION_BY_ID = gql`
  query GetReservationById($id: String!) {
    getReservationById(id: $id) {
      status
      message
      data {
        totalPrice
        id
        status
        accomodation {
          id
          name
        }
        client {
          id
          firstName
          lastName
          phone
          email
        }
        docReservation {
          id
          docUrl
        }
      }
    }
  }
`;

// GraphQL Mutation to update reservation
const UPDATE_RESERVATION = gql`
  mutation UpdateReservationById($id: String!, $updateReservationInput: UpdateReservationInput!) {
    updateReservationById(id: $id, updateReservationInput: $updateReservationInput) {
      data {
        id
        departureDate
        totalPrice
        confirmDate
        status
      }
      status
      message
    }
  }
`;

// GraphQL Mutation to delete reservation
const DELETE_RESERVATION = gql`
  mutation DeleteReservationById($id: String!) {
    deleteReservationById(id: $id) {
      status
      message
    }
  }
`;

// GraphQL Mutation to create client with reservation
const CREATE_CLIENT = gql`
  mutation CreateClient($createClientInput: CreateClientInput!) {
    createClient(createClientInput: $createClientInput) {
      id
      firstName
      lastName
      country
      email
      phone
    
    }
  }
`;

// Function to create client with reservation
const createClient = async (variables) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: CREATE_CLIENT,
      variables
    });
    return data.createClient;
  } catch (error) {
    console.error("Error creating client:", error);
    throw error;
  }
};

// Function to get reservation by ID
const getReservationById = async (id) => {
  try {
    console.log("Fetching reservation with ID:", id);
    const { data } = await apolloClient.query({
      query: GET_RESERVATION_BY_ID,
      variables: { id },
      fetchPolicy: 'no-cache'
    });
    console.log("Reservation data received:", data);
    return data.getReservationById;
  } catch (error) {
    console.error("Error fetching reservation by ID:", error);
    alert("Erreur lors de la récupération de la réservation: " + error.message);
    throw error;
  }
};

// Function to update reservation
const updateReservation = async (id, updateData) => {
  try {
    console.log("Updating reservation with ID:", id, "with data:", updateData);
    const { data } = await apolloClient.mutate({
      mutation: UPDATE_RESERVATION,
      variables: { id, updateReservationInput: updateData }
    });
    console.log("Update response:", data);
    return data.updateReservationById;
  } catch (error) {
    console.error("Error updating reservation:", error);
    alert("Erreur lors de la mise à jour de la réservation: " + error.message);
    throw error;
  }
};

// Function to delete reservation
const deleteReservation = async (id) => {
  try {
    console.log("Deleting reservation with ID:", id);
    const { data } = await apolloClient.mutate({
      mutation: DELETE_RESERVATION,
      variables: { id }
    });
    console.log("Delete response:", data);
    return data.deleteReservationById;
  } catch (error) {
    console.error("Error deleting reservation:", error);
    alert("Erreur lors de la suppression de la réservation: " + error.message);
    throw error;
  }
};

// Reservation data
const reservations = ref([]);

// Accommodations data
const accommodations = ref([]);

// Stats data
const statsData = ref({
  totalConfirmed: 0,
  guesthouseConfirmedCount: 0,
  campingConfirmedCount: 0,
  totalConfirmedAmount: 0,
  guesthousePendingCount: 0,
  campingPendingCount: 0
});

// Fetch reservations
const fetchReservations = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ALL_RESERVATIONS,
      fetchPolicy: 'no-cache',
    });
    console.log("All reservations data:", data);
    if (data?.getAllReservations?.data) {
      reservations.value = data.getAllReservations.data.map(res => ({
        id: res.id,
        client: {
          nom: res.client.firstName + " " + res.client.lastName,
          email: res.client.email,
          telephone: res.client.phone,
          nationalite: res.client.country || "Non spécifié"
        },
        maison: {
          nom: res.accomodation.name,
          ville: res.accomodation.region, // Using region as city
          capacite: 0, // Not provided
          type: "maison" // Default
        },
        dates: {
          arrivee: res.arriveDate,
          depart: res.departureDate,
          nuits: Math.ceil((new Date(res.departureDate) - new Date(res.arriveDate)) / (1000 * 60 * 60 * 24))
        },
        details: {
          adultes: 0,
          enfants: 0,
          animaux: false,
          commentaires: ""
        },
        statut: res.status || "En attente", // Use actual status from API
        prixTotal: res.totalPrice,   
        dateDemande: res.docReservation?.createdAt || "",
        messages: []
      }));
      console.log("Processed reservations:", reservations.value);
    }
  } catch (error) {
    console.error("Error fetching reservations:", error);
  }
};

// Fetch accommodations
const fetchAccommodations = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ACCOMODATIONS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getAccomodations?.data) {
      accommodations.value = data.getAccomodations.data;
    }
  } catch (error) {
    console.error("Error fetching accommodations:", error);
  }
};

// Fetch reservation stats
const fetchStats = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_RESERVATION_STATS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getReservationStats) {
      statsData.value = data.getReservationStats;
    }
  } catch (error) {
    console.error("Error fetching stats:", error);
  }
};

const showReservationModal = ref(false);
const showStatusModal = ref(false);
const showConfirmModal = ref(false);
const showPromptModal = ref(false);
const showAddReservationModal = ref(false);
const selectedReservation = ref(null);
const filterStatus = ref("Toutes");
const filterType = ref("Tous");

// Add reservation form data
const newReservation = reactive({
  client: {
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  },
  reservation: {
    adultsNum: 1,
    kidsNum: 0,
    arriveDate: "",
    departureDate: "",
    totalPrice: 0,
    accomodationId: "",
    reservedBy: "ADMIN"
  }
});

// Modal data
const modalData = ref({
  title: '',
  message: '',
  type: 'confirm',
  confirmText: 'Confirmer',
  cancelText: 'Annuler',
  inputLabel: '',
  inputPlaceholder: '',
  inputValue: '',
  selectLabel: '',
  selectOptions: [],
  selectedValue: '',
  onConfirm: null,
  onCancel: null
});


// Computed properties for statistics
const stats = computed(() => {
  const total = statsData.value.totalConfirmed;
  const enAttente = statsData.value.guesthousePendingCount + statsData.value.campingPendingCount;
  const confirmees = statsData.value.guesthouseConfirmedCount + statsData.value.campingConfirmedCount;
  const refusees = 0; // Not provided
  const revenuTotal = statsData.value.totalConfirmedAmount;

  return { total, enAttente, confirmees, refusees, revenuTotal };
});

// Filtered reservations
const filteredReservations = computed(() => {
  let filtered = reservations.value;

  // Filter by status
  if (filterStatus.value !== "Toutes") {
    filtered = filtered.filter(r => r.statut === filterStatus.value);
  }

  // Filter by accommodation type
  if (filterType.value !== "Tous") {
    filtered = filtered.filter(r => r.maison.type === filterType.value);
  }

  return filtered;
});

// Functions
const openReservationModal = async (reservation) => {
  try {
    // Fetch detailed reservation data from API
    const reservationData = await getReservationById(reservation.id);
    if (reservationData?.data) {
      selectedReservation.value = {
        ...reservation,
        // Update with actual data from API
        client: {
          nom: reservationData.data.client.firstName + " " + reservationData.data.client.lastName,
          email: reservationData.data.client.email,
          telephone: reservationData.data.client.phone,
          nationalite: "Non spécifié" // Not provided in API
        },
        maison: {
          nom: reservationData.data.accomodation.name,
          ville: "Non spécifié", // Not provided in API
          capacite: 0,
          type: "maison"
        },
        statut: reservationData.data.status || reservation.statut
      };
    } else {
      selectedReservation.value = reservation;
    }
  } catch (error) {
    console.error("Error fetching reservation details:", error);
    selectedReservation.value = reservation;
  }
  showReservationModal.value = true;
};

const closeReservationModal = () => {
  showReservationModal.value = false;
  selectedReservation.value = null;
};

 const openAddReservationModal = () => {
   Object.assign(newReservation, {
     client: {
       firstName: "",
       lastName: "",
       email: "",
       phone: ""
     },
     reservation: {
       adultsNum: 1,
       kidsNum: 0,
       arriveDate: "",
       departureDate: "",
       totalPrice: 0,
       accomodationId: "",
       reservedBy: "ADMIN"
     }
   });
  
   showAddReservationModal.value = true;
 };

const closeAddReservationModal = () => {
  showAddReservationModal.value = false;
};

const saveReservation = async () => {
  try {
    await createClient({
      createClientInput: {
        firstName: newReservation.client.firstName,
        lastName: newReservation.client.lastName,
        email: newReservation.client.email,
        phone: newReservation.client.phone,
        country: newReservation.client.country || "Non spécifié",
        reservation: {
          adultsNum: newReservation.reservation.adultsNum,
          kidsNum: newReservation.reservation.kidsNum,
          arriveDate: newReservation.reservation.arriveDate,
          departureDate: newReservation.reservation.departureDate,
          totalPrice: newReservation.reservation.totalPrice,
          accomodationId: newReservation.reservation.accomodationId,
          reservedBy: newReservation.reservation.reservedBy,
          status: "PENDING"
        }
      }
    });

    showAddReservationModal.value = false;
    alert("✅ Réservation créée avec succès");
    
    // Refresh the reservations list
    await fetchReservations();

  } catch (error) {
    console.error("Erreur réservation :", error);
    alert("❌ Erreur lors de la création de la réservation");
  }
};


const accepterReservation = async (reservation) => {
  modalData.value = {
    title: 'Confirmer la réservation',
    message: `Confirmer la réservation de ${reservation.client.nom} pour ${reservation.maison.nom} ?\n\nID de la réservation: ${reservation.id}`,
    type: 'confirm',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    onConfirm: async () => {
      try {
        // Update reservation status to CONFIRMED
        const result = await updateReservation(reservation.id, {
          status: "CONFIRMED",
          totalPrice: reservation.prixTotal,
          confirmDate: new Date().toISOString()
        });
        
        if (result?.status === 200 || result?.status === "success") {
          reservation.statut = "Confirmée";
          // Add confirmation message
          reservation.messages.push({
            auteur: "HS Travel",
            message: `Votre réservation pour ${reservation.maison.nom} a été confirmée. Nous vous contacterons bientôt pour les détails du paiement.`,
            date: new Date().toLocaleString('fr-FR'),
            type: "admin"
          });
          alert("✅ Réservation confirmée avec succès");
        } else {
          alert(`❌ Erreur lors de la confirmation de la réservation: ${result?.message || 'ID non trouvé'}`);
        }
      } catch (error) {
        console.error("Error confirming reservation:", error);
        alert("❌ Erreur lors de la confirmation de la réservation");
      }
      showConfirmModal.value = false;
    },
    onCancel: () => {
      showConfirmModal.value = false;
    }
  };
  showConfirmModal.value = true;
};

const refuserReservation = async (reservation) => {
  modalData.value = {
    title: 'Refuser la réservation',
    message: `Êtes-vous sûr de vouloir refuser la réservation de ${reservation.client.nom} pour ${reservation.maison.nom} ?\n\nID de la réservation: ${reservation.id}`,
    type: 'confirm',
    confirmText: 'Refuser',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-danger',
    onConfirm: async () => {
      try {
        // Update reservation status to CANCELLED
        const result = await updateReservation(reservation.id, {
          status: "CANCELLED"
        });
        
        if (result?.status === 200 || result?.status === "success") {
          reservation.statut = "Refusée";
          // Add refusal message
          reservation.messages.push({
            auteur: "HS Travel",
            message: `Nous sommes désolés, mais votre demande de réservation pour ${reservation.maison.nom} a été refusée.`,
            date: new Date().toLocaleString('fr-FR'),
            type: "admin"
          });
          alert("✅ Réservation refusée avec succès");
        } else {
          alert(`❌ Erreur lors du refus de la réservation: ${result?.message || 'ID non trouvé'}`);
        }
      } catch (error) {
        console.error("Error refusing reservation:", error);
        alert("❌ Erreur lors du refus de la réservation");
      }
      showConfirmModal.value = false;
    },
    onCancel: () => {
      showConfirmModal.value = false;
    }
  };
  showConfirmModal.value = true;
};


const modifierStatut = async (reservation) => {
  const statutActuel = reservation.statut;
  const nouveauxStatuts = ["En attente", "Confirmée", "Refusée"];
  const autresStatuts = nouveauxStatuts.filter(s => s !== statutActuel);

  modalData.value = {
    title: 'Modifier le statut',
    message: `Changer le statut de "${statutActuel}" vers :\n\nID de la réservation: ${reservation.id}`,
    type: 'select',
    confirmText: 'Modifier',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-warning',
    selectLabel: 'Nouveau statut',
    selectOptions: autresStatuts.map(statut => ({ value: statut, label: statut })),
    selectedValue: autresStatuts[0],
    onConfirm: async (nouveauStatut) => {
      if (nouveauStatut && nouveauStatut !== statutActuel) {
        // Confirmation finale
        modalData.value = {
          title: 'Confirmer le changement',
          message: `Confirmer le changement de statut de "${statutActuel}" vers "${nouveauStatut}" pour la réservation de ${reservation.client.nom} ?`,
          type: 'confirm',
          confirmText: 'Confirmer',
          cancelText: 'Annuler',
          onConfirm: async () => {
            try {
              // Map frontend status to backend status
              let backendStatus = "";
              if (nouveauStatut === "Confirmée") backendStatus = "CONFIRMED";
              else if (nouveauStatut === "Refusée") backendStatus = "CANCELLED";
              else if (nouveauStatut === "En attente") backendStatus = "PENDING";

              const result = await updateReservation(reservation.id, {
                status: backendStatus
              });

              if (result?.status === 200 || result?.status === "success") {
                reservation.statut = nouveauStatut;

                // Ajouter un message système selon le nouveau statut
                let messageSysteme = "";
                if (nouveauStatut === "Confirmée") {
                  messageSysteme = `Votre réservation pour ${reservation.maison.nom} a été confirmée. Nous vous contacterons bientôt pour les détails du paiement.`;
                } else if (nouveauStatut === "Refusée") {
                  // Add refusal message without asking for reason
                  reservation.messages.push({
                    auteur: "HS Travel",
                    message: `Nous sommes désolés, mais votre demande de réservation pour ${reservation.maison.nom} a été refusée.`,
                    date: new Date().toLocaleString('fr-FR'),
                    type: "admin"
                  });
                } else if (nouveauStatut === "En attente") {
                  messageSysteme = `Le statut de votre réservation pour ${reservation.maison.nom} a été remis en attente.`;
                }

                if (messageSysteme) {
                  reservation.messages.push({
                    auteur: "HS Travel",
                    message: messageSysteme,
                    date: new Date().toLocaleString('fr-FR'),
                    type: "admin"
                  });
                }

                alert("✅ Statut mis à jour avec succès");
              } else {
                alert(`❌ Erreur lors de la mise à jour du statut: ${result?.message || 'ID non trouvé'}`);
              }
            } catch (error) {
              console.error("Error updating reservation status:", error);
              alert("❌ Erreur lors de la mise à jour du statut");
            }
            showConfirmModal.value = false;
          },
          onCancel: () => {
            showConfirmModal.value = false;
          }
        };
        showConfirmModal.value = true;
      }
      showStatusModal.value = false;
    },
    onCancel: () => {
      showStatusModal.value = false;
    }
  };
  showStatusModal.value = true;
};

// Nouvelle fonction pour mettre à jour une réservation avec plus de détails
const updateReservationDetails = async (reservation) => {
  // Données initiales pour le formulaire
  const updateData = reactive({
    adultsNum: 1,
    totalPrice: reservation.prixTotal || 0,
    confirmDate: "",
    status: reservation.statut === "Confirmée" ? "CONFIRMED" :
             reservation.statut === "Refusée" ? "CANCELLED" : "PENDING"
  });

  modalData.value = {
    title: 'Mettre à jour la réservation',
    message: `Mettre à jour les détails de la réservation pour ${reservation.client.nom} :\n\nID: ${reservation.id}`,
    type: 'form',
    confirmText: 'Mettre à jour',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-primary',
    formFields: [
      {
        label: 'Nombre d\'adultes',
        type: 'number',
        model: 'adultsNum',
        min: 1,
        value: updateData.adultsNum
      },
      {
        label: 'Prix total (€)',
        type: 'number',
        model: 'totalPrice',
        step: '0.01',
        min: 0,
        value: updateData.totalPrice
      },
      {
        label: 'Date de confirmation',
        type: 'date',
        model: 'confirmDate',
        value: updateData.confirmDate
      },
      {
        label: 'Statut',
        type: 'select',
        model: 'status',
        options: [
          { value: 'PENDING', label: 'En attente' },
          { value: 'CONFIRMED', label: 'Confirmée' },
          { value: 'CANCELLED', label: 'Refusée' }
        ],
        value: updateData.status
      }
    ],
    onConfirm: async () => {
      try {
        const result = await updateReservation(reservation.id, {
          adultsNum: updateData.adultsNum,
          totalPrice: updateData.totalPrice,
          confirmDate: updateData.confirmDate || undefined,
          status: updateData.status
        });

        if (result?.status === 200 || result?.status === "success") {
          // Mettre à jour les données locales
          reservation.prixTotal = updateData.totalPrice;
          
          // Mettre à jour le statut affiché
          if (updateData.status === "CONFIRMED") {
            reservation.statut = "Confirmée";
          } else if (updateData.status === "CANCELLED") {
            reservation.statut = "Refusée";
          } else {
            reservation.statut = "En attente";
          }

          alert("✅ Réservation mise à jour avec succès");
        } else {
          alert(`❌ Erreur lors de la mise à jour: ${result?.message || 'ID non trouvé'}`);
        }
      } catch (error) {
        console.error("Error updating reservation:", error);
        alert("❌ Erreur lors de la mise à jour de la réservation");
      }
      showStatusModal.value = false;
    },
    onCancel: () => {
      showStatusModal.value = false;
    }
  };
  showStatusModal.value = true;
};

const getStatusColor = (status) => {
  switch (status) {
    case "CONFIRMED": return "success";
    case "PENDING": return "warning";
    case "CANCELLED": return "danger";
    default: return "secondary";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "CONFIRMED": return "fa-check-circle";
    case "PENDING": return "fa-clock";
    case "CANCELLED": return "fa-times-circle";
    default: return "fa-question-circle";
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case "GUESTHOUSE": return "Maison d'hôte";
    case "CAMPING": return "Campement";
    default: return type;
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case "GUESTHOUSE": return "fa-home";
    case "CAMPING": return "fa-campground";
    default: return "fa-question-circle";
  }
};

// Function to delete reservation
const supprimerReservation = async (reservation) => {
  modalData.value = {
    title: 'Supprimer la réservation',
    message: `Êtes-vous sûr de vouloir supprimer la réservation de ${reservation.client.nom} pour ${reservation.maison.nom} ? Cette action est irréversible.\n\nID de la réservation: ${reservation.id}`,
    type: 'confirm',
    confirmText: 'Supprimer',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-danger',
    onConfirm: async () => {
      try {
        const result = await deleteReservation(reservation.id);
        
        if (result?.status === 200 || result?.status === "success") {
          // Remove from local list
          reservations.value = reservations.value.filter(r => r.id !== reservation.id);
          alert("✅ Réservation supprimée avec succès");
        } else {
          alert(`❌ Erreur lors de la suppression de la réservation: ${result?.message || 'ID non trouvé'}`);
        }
      } catch (error) {
        console.error("Error deleting reservation:", error);
        alert("❌ Erreur lors de la suppression de la réservation");
      }
      showConfirmModal.value = false;
    },
    onCancel: () => {
      showConfirmModal.value = false;
    }
  };
  showConfirmModal.value = true;
};

onMounted(() => {
  fetchReservations();
  fetchAccommodations();
  fetchStats();
});
</script>

<template>
    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">Total Réservations</p>
                  <h5 class="font-weight-bolder">{{ stats.total }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-primary shadow-primary text-center border-radius-md">
                  <i class="ni ni-calendar-grid-58 text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">En Attente</p>
                  <h5 class="font-weight-bolder">{{ stats.enAttente }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-warning shadow-warning text-center border-radius-md">
                  <i class="ni ni-time-alarm text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">Confirmées</p>
                  <h5 class="font-weight-bolder">{{ stats.confirmees }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-success shadow-success text-center border-radius-md">
                  <i class="ni ni-check-bold text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">Revenus Totaux</p>
                  <h5 class="font-weight-bolder">{{ stats.totalConfirmedAmount || 0 }} DT </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-info shadow-info text-center border-radius-md">
                  <i class="ni ni-money-coins text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters and Actions -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <h6>Gestion des Réservations</h6>
              <div class="d-flex align-items-center flex-wrap gap-3">
                <button class="btn btn-primary btn-sm" @click="openAddReservationModal">
                  <i class="ni ni-fat-add"></i> Ajouter
                </button>
                <div class="d-flex align-items-center">
                  <label class="form-label me-2 mb-0">Type d'hébergement:</label>
                  <select v-model="filterType" class="form-select form-select-sm" style="width: auto;">
                    <option value="Tous">Tous</option>
                    <option value="maison">Maisons d'hôte</option>
                    <option value="campement">Campements</option>
                  </select>
                </div>
                <div class="d-flex align-items-center">
                  <label class="form-label me-2 mb-0">Statut:</label>
                  <select v-model="filterStatus" class="form-select form-select-sm" style="width: auto;">
                    <option value="Toutes">Toutes</option>
                    <option value="En attente">En attente</option>
                    <option value="Confirmée">Confirmées</option>
                    <option value="Refusée">Refusées</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reservations Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Client
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Maison d'hôte
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Type
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Dates
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Statut
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Prix
                    </th>
                    <th class="text-secondary opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in filteredReservations" :key="reservation.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div>
                       
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ reservation.client.nom }}</h6>
                          <p class="text-xs text-secondary mb-0">
                            {{ reservation.client.email }}
                          </p>
                          <small class="text-muted">{{ reservation.client.nationalite }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ reservation.maison.nom }}</p>
                      <p class="text-xs text-secondary mb-0">{{ reservation.maison.ville }}</p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm bg-gradient-info">
                        <i :class="`fa ${getTypeIcon(reservation.maison.type)} me-1`"></i>
                        {{ getTypeLabel(reservation.maison.type) }}
                      </span>
                    </td>
                    <td>
                      <div class="d-flex flex-column">
                        <span class="text-xs font-weight-bold">{{ reservation.dates.arrivee }}</span>
                        <span class="text-xs text-secondary">au</span>
                        <span class="text-xs font-weight-bold">{{ reservation.dates.depart }}</span>
                        <small class="text-muted">{{ reservation.dates.nuits }} nuits</small>
                      </div>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span
                        class="badge badge-sm"
                        :class="`bg-gradient-${getStatusColor(reservation.statut)}`"
                      >
                        <i :class="`fa ${getStatusIcon(reservation.statut)} me-1`"></i>
                        {{ reservation.statut }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{ reservation.totalPrice || 0 }} DT
                      </span>
                    </td>
                    <td class="align-middle">
                      <div class="d-flex">
                        <button
                          class="btn btn-link text-info mb-0 me-2"
                          @click="openReservationModal(reservation)"
                          title="Voir détails"
                        >
                          <i class="fa fa-eye text-xs"></i>
                        </button>
                        <button
                          v-if="reservation.statut === 'En attente'"
                          class="btn btn-link text-success mb-0 me-2"
                          @click="accepterReservation(reservation)"
                          title="Accepter"
                        >
                          <i class="fa fa-check text-xs"></i>
                        </button>
                        <button
                          v-if="reservation.statut === 'En attente'"
                          class="btn btn-link text-danger mb-0 me-2"
                          @click="refuserReservation(reservation)"
                          title="Refuser"
                        >
                          <i class="fa fa-times text-xs"></i>
                        </button>
                        <button
                          class="btn btn-link text-warning mb-0 me-2"
                          @click="modifierStatut(reservation)"
                          title="Modifier statut"
                        >
                          <i class="fa fa-edit text-xs"></i>
                        </button>
                        <button
                          class="btn btn-link text-info mb-0 me-2"
                          @click="updateReservationDetails(reservation)"
                          title="Mettre à jour"
                        >
                          <i class="fa fa-sync text-xs"></i>
                        </button>
                        <button
                          class="btn btn-link text-danger mb-0 me-2"
                          @click="supprimerReservation(reservation)"
                          title="Supprimer"
                        >
                          <i class="fa fa-trash text-xs"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reservation Details Modal -->
    <div v-if="showReservationModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Détails de la Réservation #{{ selectedReservation?.id }}
            </h5>
            <button type="button" class="btn-close" @click="closeReservationModal"></button>
          </div>
          <div v-if="selectedReservation" class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6>Informations Client</h6>
                <div class="card p-3 mb-3">
                  <p><strong>Nom:</strong> {{ selectedReservation.client.nom }}</p>
                  <p><strong>Email:</strong> {{ selectedReservation.client.email }}</p>
                  <p><strong>Téléphone:</strong> {{ selectedReservation.client.telephone }}</p>
                  <p><strong>Nationalité:</strong> {{ selectedReservation.client.nationalite }}</p>
                </div>

                <h6>Détails du Séjour</h6>
                <div class="card p-3">
                  <p><strong>Maison:</strong> {{ selectedReservation.maison.nom }}</p>
                  <p><strong>Ville:</strong> {{ selectedReservation.maison.ville }}</p>
                  <p><strong>Type:</strong>
                    <span class="badge bg-gradient-info ms-2">
                      <i :class="`fa ${getTypeIcon(selectedReservation.maison.type)} me-1`"></i>
                      {{ getTypeLabel(selectedReservation.maison.type) }}
                    </span>
                  </p>
                  <p><strong>Capacité:</strong> {{ selectedReservation.maison.capacite }} personnes</p>
                  <p><strong>Arrivée:</strong> {{ selectedReservation.dates.arrivee }}</p>
                  <p><strong>Départ:</strong> {{ selectedReservation.dates.depart }}</p>
                  <p><strong>Nombre de nuits:</strong> {{ selectedReservation.dates.nuits }}</p>
                  <p><strong>Adultes:</strong> {{ selectedReservation.details.adultes }}</p>
                  <p><strong>Enfants:</strong> {{ selectedReservation.details.enfants }}</p>
                  <p><strong>Animaux:</strong> {{ selectedReservation.details.animaux ? 'Oui' : 'Non' }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <h6>Statut et Prix</h6>
                <div class="card p-3 mb-3">
                  <p><strong>Statut:</strong>
                    <span
                      class="badge ms-2"
                      :class="`bg-gradient-${getStatusColor(selectedReservation.statut)}`"
                    >
                      {{ selectedReservation.statut }}
                    </span>
                  </p>
                  <p><strong>Prix total:</strong> {{ selectedReservation.totalPrice }} DT</p>
                  <p><strong>Date de demande:</strong> {{ selectedReservation.dateDemande }}</p>
                  <p v-if="selectedReservation.motifRefus">
                    <strong>Motif de refus:</strong> {{ selectedReservation.motifRefus }}
                  </p>
                </div>

                <h6>Commentaires</h6>
                <div class="card p-3">
                  <p>{{ selectedReservation.details.commentaires }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeReservationModal">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Modals -->
      <CustomModal
        v-model:show="showConfirmModal"
        :title="modalData.title"
        :message="modalData.message"
        :type="modalData.type"
        :confirm-text="modalData.confirmText"
        :cancel-text="modalData.cancelText"
        :confirm-button-class="modalData.confirmButtonClass"
        :input-label="modalData.inputLabel"
        :input-placeholder="modalData.inputPlaceholder"
        :input-value="modalData.inputValue"
        :select-label="modalData.selectLabel"
        :select-options="modalData.selectOptions"
        :selected-value="modalData.selectedValue"
        @confirm="modalData.onConfirm"
        @cancel="modalData.onCancel"
      />
  
      <CustomModal
        v-model:show="showPromptModal"
        :title="modalData.title"
        :message="modalData.message"
        :type="modalData.type"
        :confirm-text="modalData.confirmText"
        :cancel-text="modalData.cancelText"
        :confirm-button-class="modalData.confirmButtonClass"
        :input-label="modalData.inputLabel"
        :input-placeholder="modalData.inputPlaceholder"
        :input-value="modalData.inputValue"
        @confirm="modalData.onConfirm"
        @cancel="modalData.onCancel"
      />
  
      <CustomModal
        v-model:show="showStatusModal"
        :title="modalData.title"
        :message="modalData.message"
        :type="modalData.type"
        :confirm-text="modalData.confirmText"
        :cancel-text="modalData.cancelText"
        :confirm-button-class="modalData.confirmButtonClass"
        :select-label="modalData.selectLabel"
        :select-options="modalData.selectOptions"
        :selected-value="modalData.selectedValue"
        :form-fields="modalData.formFields"
        @confirm="modalData.onConfirm"
        @cancel="modalData.onCancel"
      />

    <!-- Modal for Add Reservation -->
    <div v-if="showAddReservationModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Ajouter une Réservation</h5>
            <button type="button" class="btn-close" @click="closeAddReservationModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveReservation">
              <h6>Informations Client</h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Prénom</label>
                    <input
                      v-model="newReservation.client.firstName"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nom</label>
                    <input
                      v-model="newReservation.client.lastName"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Email</label>
                    <input
                      v-model="newReservation.client.email"
                      type="email"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Téléphone</label>
                    <input
                      v-model="newReservation.client.phone"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Pays</label>
                    <input
                      v-model="newReservation.client.country"
                      type="text"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
              </div>
              <h6>Détails de la Réservation</h6>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre d'adultes</label>
                    <input
                      v-model.number="newReservation.reservation.adultsNum"
                      type="number"
                      class="form-control"
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre d'enfants</label>
                    <input
                      v-model.number="newReservation.reservation.kidsNum"
                      type="number"
                      class="form-control"
                      min="0"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Date d'arrivée</label>
                    <input
                      v-model="newReservation.reservation.arriveDate"
                      type="datetime-local"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Date de départ</label>
                    <input
                      v-model="newReservation.reservation.departureDate"
                      type="datetime-local"
                      class="form-control"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Prix total</label>
                    <input
                      v-model.number="newReservation.reservation.totalPrice"
                      type="number"
                      step="0.01"
                      class="form-control"
                      min="0"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Maison d'hôte</label>
                    <select
                      v-model="newReservation.reservation.accomodationId"
                      class="form-control"
                      required
                    >
                      <option value="">Sélectionner une maison d'hôte</option>
                      <option v-for="acc in accommodations.filter(a => a.type === 'GUESTHOUSE')" :key="acc.id" :value="acc.id">
                        {{ acc.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Campement</label>
                    <select
                      v-model="newReservation.reservation.accomodationId"
                      class="form-control"
                      required
                    >
                      <option value="">Sélectionner un campement</option>
                      <option v-for="acc in accommodations.filter(a => a.type === 'CAMPING')" :key="acc.id" :value="acc.id">
                        {{ acc.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddReservationModal">
              Annuler
            </button>
            <button type="button" class="btn btn-primary" @click="saveReservation">
              Ajouter
            </button>
          </div>
        </div>
      </div>
    </div>

    </template>
